/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FADE_UP_SM } from '@/lib/motionPresets';
import { CreditCard, Save, Trash2, KeyRound, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { StripeStatusCard, StripeLiveAlert, StripeGuideCard } from '@/components/admin/stripe/StripeStatusCards';
import { SecretInput } from '@/components/admin/stripe/SecretInput';

/**
 * StripeConfigPage — admin-only page for white-label clinics to configure
 * their own Stripe keys, stored in MongoDB (not .env). Overrides env vars.
 *
 * Endpoints:
 *   GET    /api/admin/stripe-config     — returns masked config + status
 *   PUT    /api/admin/stripe-config     — saves keys (server never returns clear text after)
 *   DELETE /api/admin/stripe-config     — clears config (falls back to env)
 *
 * Le card di stato/guida e il campo segreto vivono in `components/admin/stripe/*`.
 */
const StripeConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [status, setStatus] = useState(null); // masked view

  // Form state — kept empty by default; user pastes secrets on demand.
  const [apiKey, setApiKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/stripe-config');
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel caricamento della configurazione Stripe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    // Only send fields the user actually typed (empty string clears; null leaves unchanged)
    const payload = {};
    if (apiKey.trim() !== '') payload.api_key = apiKey.trim();
    if (publishableKey.trim() !== '') payload.publishable_key = publishableKey.trim();
    if (webhookSecret.trim() !== '') payload.webhook_secret = webhookSecret.trim();
    if (Object.keys(payload).length === 0) {
      toast.info('Nessuna chiave da salvare. Incolla almeno un valore.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/stripe-config', payload);
      setStatus(res.data);
      // Clear inputs after save so keys are not lingering in memory
      setApiKey(''); setPublishableKey(''); setWebhookSecret('');
      toast.success(`Configurazione salvata (${res.data.mode.toUpperCase()} mode)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Rimuovere completamente le chiavi Stripe? Nessuna nuova visita a pagamento sarà possibile finché non le riconfiguri.')) return;
    setClearing(true);
    try {
      const res = await api.delete('/admin/stripe-config');
      setStatus(res.data);
      toast.success('Configurazione Stripe rimossa');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella rimozione');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="stripe-config-page">
      <motion.div {...FADE_UP_SM} className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-primary" /> Configurazione Stripe
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Ogni clinica configura le proprie chiavi Stripe. I pagamenti dei tuoi pazienti finiranno
            <strong className="text-foreground"> sul tuo IBAN Stripe</strong>, non su quello del fornitore del software.
          </p>
        </div>
        <a
          href="https://dashboard.stripe.com/apikeys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          data-testid="stripe-dashboard-link"
        >
          Apri Dashboard Stripe <ExternalLink className="h-3 w-3" />
        </a>
      </motion.div>

      <StripeStatusCard status={status} loading={loading} />
      {status?.mode === 'live' && <StripeLiveAlert />}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" /> Nuove chiavi
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Lascia vuoti i campi che non vuoi modificare. Solo i campi compilati verranno salvati.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <SecretInput id="stripe-api-key" label="Secret key" hint="(sk_test_… oppure sk_live_…)" value={apiKey} onChange={setApiKey}
            placeholder="sk_live_…" testId="stripe-input-api-key" toggleTestId="stripe-toggle-secret" />
          <SecretInput id="stripe-pub-key" label="Publishable key" hint="(pk_test_… oppure pk_live_…)" value={publishableKey} onChange={setPublishableKey}
            placeholder="pk_live_…" testId="stripe-input-publishable-key" secret={false} />
          <SecretInput id="stripe-webhook" label="Webhook secret" hint="(whsec_…)" value={webhookSecret} onChange={setWebhookSecret}
            placeholder="whsec_…" testId="stripe-input-webhook-secret" toggleTestId="stripe-toggle-webhook" />

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="stripe-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva chiavi
            </Button>
            {status?.configured && (
              <Button
                onClick={clear}
                disabled={clearing}
                variant="outline"
                className="text-destructive hover:text-destructive"
                data-testid="stripe-clear-btn"
              >
                {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Rimuovi tutte
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              Le chiavi sono salvate crittografate a riposo su MongoDB e non vengono mai restituite in chiaro dal server.
            </span>
          </div>
        </CardContent>
      </Card>

      <StripeGuideCard />
    </div>
  );
};

export default StripeConfigPage;
